
import { Component, Prop, Emit, Watch } from "vue-property-decorator";
import Vue from "vue";
import Draggable from "../utilities/draggable.vue";
import { NotifyNotificationRules, NotifySkipped, NotifyRuleReload, NotifySent } from "@/store/site-monitor/types";
import NotificationModalContent from "@/components/site-monitor/NotificationModalContent.vue";
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import eventsApi from "@/services/api.event.service";
import { Getter, namespace } from 'vuex-class';
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";
import { SkippedRequiredNotificationTemplate } from "@/mixins/NotificationMixin";

const SiteMonitor = namespace("siteMonitor");

@Component({
	components: {
		'draggable': Draggable,
		"notification-modal-content": NotificationModalContent
	}
})
export default class NotificationModalPopout extends Vue {
	@Prop({ type: Number }) readonly groupId: number;
	@Prop({ type: Number }) readonly eventId: number;
	@Prop({ type: Boolean}) readonly isVisible: boolean;

	@Prop({type: Boolean, required: false, default: true})
	private notifyOpenedManually: boolean;

	@Prop({ type: Object, required: false})
	private notifyNotificationRules: NotifyNotificationRules;

	@Prop({ type: Boolean, required: false, default: false })
	private isQuickEndingEvent: boolean;

	@SiteMonitor.Getter("notificationModalLayoutColumnCount") private notificationModalLayoutColumnCount: number;
	@SiteMonitor.Getter("notificationRulesTooltip") private notificationRulesTooltip: string;

	private isDraggable: boolean = true;
	private eventOutcome: EventOutcomeAndNote = {
		eventOutcomeId: null,
		eventOutcomeNote: null,
		valid: true
	};
	private selectedNotificationTemplate: NotificationTemplate | null = null;
	private modalSizeClass = 'draggable-size';

	private LARGE_LAYOUT_THRESHOLD = 2;
	private LARGE_CLASS = 'draggable-size';
	private EXTRA_LARGE_CLASS = 'draggable-size_lg';

	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@Emit("notify-sent")
	private emitNotifySent(notifySent: NotifySent): NotifySent {
		return notifySent;
	}

	@Emit("notify-skipped")
	private emitNotifySkipped(notifySkipped: NotifySkipped): NotifySkipped {
		return notifySkipped;
	}
	
	@Emit("notify-skipped-predefined-templates")
	private auditSkippedRequiredNotificationTemplates(skippedRequiredNotificationTemplates: SkippedRequiredNotificationTemplate[]): SkippedRequiredNotificationTemplate[] {
		return skippedRequiredNotificationTemplates;
	}

	@Emit("notify-rule-reload")
	private emitNotifyRuleReload(notifyRuleReload: NotifyRuleReload): NotifyRuleReload {
		return notifyRuleReload;
	}

	@Emit("cancel")
	private emitCancel(): boolean {
		return true;
	}

	@Emit("popout-closed")
	private async disablePopoutMode(): Promise<boolean> {
		await this.updateEventOutcome();
        return false;
    }

	private onFocusChanged(isFocused: boolean): void {
		this.isDraggable = !isFocused;
	}

	private get isPersistEventOutcomeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "PersistEventOutcome"]);
	}

	private get isNotificationModalLayoutEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "RearrangedLayout"]);
	}

	private async onEventOutcomeChange(value: EventOutcomeAndNote): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled) {
			this.eventOutcome = value;
		}
	}

	private async updateEventOutcome(): Promise<void> {
		if (this.eventId && this.isPersistEventOutcomeEnabled && this.selectedNotificationTemplate != null) {
			await eventsApi.preserveEventOutcome(this.eventId, 
				this.eventOutcome?.eventOutcomeId ?? null,
				this.eventOutcome?.eventOutcomeNote ?? null
			);
		}
	}

	private onTemplateChanged(newTemplate: NotificationTemplate): void {
		this.selectedNotificationTemplate = newTemplate;
	}

	@Watch("notificationModalLayoutColumnCount", { immediate: true })
	private updateModalLayout(): void {
		if (!this.isVisible) {
			return;
		}

		if (!this.isNotificationModalLayoutEnabled) {
			this.modalSizeClass = this.LARGE_CLASS;
			return;
		}

		if (this.notificationModalLayoutColumnCount >= this.LARGE_LAYOUT_THRESHOLD) {
			this.modalSizeClass = this.EXTRA_LARGE_CLASS;
		} else {
			this.modalSizeClass = this.LARGE_CLASS;
		}												
	}

	private get notifyRules(): NotificationRule[] {
		return this.notifyNotificationRules?.rules ?? null;
	}

	private get notificationRuleTooltip(): string {
		if (!this.notifyRules) {
			return "";
		}

		return this.notificationRulesTooltip;
	}
}
